/**
 * Interface-only state: selection, open dialogs and menus, editor preferences.
 * Nothing here belongs in the project document, so it never enters undo history.
 */
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { FilterInstance, Id, ItemTransform } from '../core/model';
import type { FilterOwner } from '../state/api';
import type { MenuEntry } from './components/menuModel';
import { resolveTheme, systemPrefersLight, THEME_PREFERENCES, type Theme, type ThemePreference } from './theme/theme';

export type SettingsTab = 'general' | 'video' | 'audio' | 'stream' | 'channel' | 'output' | 'hotkeys' | 'plugins' | 'about';

export type DialogSpec =
  | { type: 'properties'; sourceId: Id }
  | { type: 'filters'; owner: FilterOwner }
  | { type: 'transform'; sceneId: Id; itemId: Id }
  | { type: 'settings'; tab?: SettingsTab }
  | { type: 'recordings' }
  | { type: 'edit-stream-info' }
  | { type: 'streaming-tools' }
  | { type: 'advanced-audio' }
  | { type: 'shortcuts' }
  | { type: 'about' }
  | { type: 'collections' }
  | { type: 'add-source'; kind: string }
  | { type: 'transition-properties'; transitionId: Id }
  | { type: 'prompt'; title: string; label: string; initial: string; confirmLabel?: string; onSubmit(value: string): void }
  | { type: 'confirm'; title: string; message: string; confirmLabel?: string; danger?: boolean; onConfirm(): void };

export type OpenDialog = DialogSpec & { key: number };

export type StreamManagerFigure = 'session' | 'viewers' | 'followers' | 'bitrate' | 'health' | 'brandSafety';
export type ActivityGroup = 'stream' | 'viewers' | 'guests' | 'recording' | 'health';

export interface EditorPrefs {
  snapEnabled: boolean;
  /** Screen pixels within which an edge snaps. */
  snapDistance: number;
  snapToCanvas: boolean;
  snapToCenter: boolean;
  snapToItems: boolean;
  confirmRemoveScene: boolean;
  confirmRemoveItem: boolean;
  confirmStopStream: boolean;
  confirmStopRecording: boolean;
  /** Offer to enable system notifications for stream problems when going live. */
  offerAlerts: boolean;
  /**
   * Lists diagnostic source kinds (test signal) in Add Source and shows the
   * status-bar badge. A per-browser preference, never a project setting.
   * Read it through `isDiagnosticsMode`, which also honours the URL and dev builds.
   */
  diagnosticsMode: boolean;
  /** Figures shown in the Stream Manager's status strip. */
  streamManagerFigures: Record<StreamManagerFigure, boolean>;
  /** Event groups shown in the Stream Manager's activity feed. */
  streamManagerActivityFilter: Record<ActivityGroup, boolean>;
  /** Beep on new viewer and guest events in the activity feed. */
  streamManagerActivitySound: boolean;
  /** Interface theme; 'system' follows the operating system. A `#theme=` link or the embedding portal outranks it. */
  theme: ThemePreference;
}

export const DEFAULT_PREFS: EditorPrefs = {
  snapEnabled: true,
  snapDistance: 10,
  snapToCanvas: true,
  snapToCenter: true,
  snapToItems: true,
  confirmRemoveScene: true,
  confirmRemoveItem: false,
  confirmStopStream: true,
  confirmStopRecording: false,
  offerAlerts: true,
  diagnosticsMode: false,
  streamManagerFigures: { session: true, viewers: true, followers: true, bitrate: true, health: true, brandSafety: true },
  streamManagerActivityFilter: { stream: true, viewers: true, guests: true, recording: true, health: true },
  streamManagerActivitySound: false,
  theme: 'system',
};

/**
 * `?diagnostics=1` forces diagnostics mode on and `?diagnostics=0` forces it
 * off (so a dev build can be tested as production); anything else leaves the
 * decision to the preference and the build.
 */
export function diagnosticsOverrideFromUrl(search: string): boolean | null {
  const value = new URLSearchParams(search).get('diagnostics');
  if (value === '1' || value === 'true') return true;
  if (value === '0' || value === 'false') return false;
  return null;
}

/** Diagnostics mode: the URL override wins, then dev builds (always on), then the preference. */
export function resolveDiagnosticsMode(override: boolean | null, devBuild: boolean, preference: boolean): boolean {
  return override ?? (devBuild || preference);
}

export interface Clipboard {
  items: { sceneId: Id; itemIds: Id[] } | null;
  transform: ItemTransform | null;
  filters: FilterInstance[] | null;
}

export interface MenuState {
  x: number;
  y: number;
  items: MenuEntry[];
  /** Element to refocus when the menu closes. */
  returnFocus: HTMLElement | null;
}

export interface DockController {
  isOpen(id: string): boolean;
  toggle(id: string): void;
  /** Opens the dock if needed and brings it to the front of its group. */
  show(id: string): void;
  reset(): void;
}

export interface UiState {
  /** Item ids selected in the edited scene. */
  selection: Id[];
  collapsedGroups: Record<Id, true>;
  clipboard: Clipboard;
  dialogs: OpenDialog[];
  menu: MenuState | null;
  prefs: EditorPrefs;
  dock: DockController | null;
  /** Bumped whenever the set of open dock panels changes, so menus re-read it. */
  dockRevision: number;
  /** Output calls in flight, so buttons can disable themselves until the kernel answers. */
  busy: { streaming: boolean; recording: boolean };
  /** True while a pointer gesture in the editor is changing the document; shortcuts wait until it ends. */
  gesture: boolean;
  /** The "enable system notifications?" offer is on screen. Shown at most once per session. */
  alertOffer: 'unseen' | 'open' | 'done';
  /** `?diagnostics=` of the page URL at startup; see `diagnosticsOverrideFromUrl`. */
  diagnosticsOverride: boolean | null;
  /** `#embed=1` at startup: running inside the portal's creator dashboard. Never persisted. */
  embedded: boolean;
  /** `#theme=` at startup; replaced by a later `omp:theme` from the portal. */
  themeHash: Theme | null;
  /** The last `omp:theme` the embedding portal sent. */
  themeParent: Theme | null;
  /** Whether the OS asks for a light colour scheme; kept current by the shell. */
  systemLight: boolean;

  select(ids: Id[]): void;
  toggleCollapsed(groupId: Id): void;
  setClipboard(patch: Partial<Clipboard>): void;
  openDialog(spec: DialogSpec): void;
  closeDialog(key: number): void;
  openMenu(menu: MenuState): void;
  closeMenu(): void;
  setPrefs(patch: Partial<EditorPrefs>): void;
  setDock(dock: DockController | null): void;
  bumpDock(): void;
  setBusy(patch: Partial<UiState['busy']>): void;
  setGesture(active: boolean): void;
  setAlertOffer(state: UiState['alertOffer']): void;
  /** The portal's theme wins from now on, over the starting `#theme=` too. */
  setParentTheme(theme: Theme): void;
  setSystemLight(light: boolean): void;
}

export type UiStore = StoreApi<UiState>;

/** Selector: whether diagnostic source kinds are offered and the diagnostics badge shows. */
export function isDiagnosticsMode(state: UiState): boolean {
  return resolveDiagnosticsMode(state.diagnosticsOverride, import.meta.env.DEV, state.prefs.diagnosticsMode);
}

/** Selector: the theme the interface shows. */
export function selectTheme(state: UiState): Theme {
  return resolveTheme({ hash: state.themeHash, parent: state.themeParent, preference: state.prefs.theme, systemLight: state.systemLight });
}

function urlSearch(): string {
  try {
    return typeof location === 'undefined' ? '' : location.search;
  } catch {
    return '';
  }
}

/** A copy of the defaults whose nested maps are not shared with DEFAULT_PREFS. */
function defaultPrefs(): EditorPrefs {
  return {
    ...DEFAULT_PREFS,
    streamManagerFigures: { ...DEFAULT_PREFS.streamManagerFigures },
    streamManagerActivityFilter: { ...DEFAULT_PREFS.streamManagerActivityFilter },
  };
}

const PREFS_KEY = 'ohmyobs.ui.prefs.v1';

export function loadPrefs(storage: Pick<Storage, 'getItem'> | null): EditorPrefs {
  try {
    const raw = storage?.getItem(PREFS_KEY);
    if (!raw) return defaultPrefs();
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return defaultPrefs();
    const out: EditorPrefs = defaultPrefs();
    const bag = parsed as Record<string, unknown>;
    for (const key of Object.keys(DEFAULT_PREFS) as (keyof EditorPrefs)[]) {
      const value = bag[key];
      const fallback = DEFAULT_PREFS[key];
      if (typeof fallback === 'object') {
        // Boolean maps: keep known keys with boolean values, so a new key gets its default.
        if (!value || typeof value !== 'object') continue;
        const merged: Record<string, boolean> = { ...fallback };
        for (const [name, on] of Object.entries(value as Record<string, unknown>)) {
          if (name in fallback && typeof on === 'boolean') merged[name] = on;
        }
        (out as unknown as Record<string, unknown>)[key] = merged;
      } else if (typeof value === typeof fallback) {
        (out as unknown as Record<string, unknown>)[key] = value;
      }
    }
    out.snapDistance = Math.min(50, Math.max(1, out.snapDistance));
    if (!THEME_PREFERENCES.includes(out.theme)) out.theme = DEFAULT_PREFS.theme;
    return out;
  } catch {
    return defaultPrefs();
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export interface UiStoreOptions {
  embedded?: boolean;
  theme?: Theme | null;
}

export function createUiStore(options: UiStoreOptions = {}): UiStore {
  const storage = safeStorage();
  let nextKey = 1;
  return createStore<UiState>((set, get) => ({
    selection: [],
    collapsedGroups: {},
    clipboard: { items: null, transform: null, filters: null },
    dialogs: [],
    menu: null,
    prefs: loadPrefs(storage),
    dock: null,
    dockRevision: 0,
    busy: { streaming: false, recording: false },
    gesture: false,
    alertOffer: 'unseen',
    diagnosticsOverride: diagnosticsOverrideFromUrl(urlSearch()),
    embedded: options.embedded ?? false,
    themeHash: options.theme ?? null,
    themeParent: null,
    systemLight: systemPrefersLight(),

    select(ids) {
      const current = get().selection;
      if (current.length === ids.length && current.every((id, i) => id === ids[i])) return;
      set({ selection: ids });
    },
    toggleCollapsed(groupId) {
      const next = { ...get().collapsedGroups };
      if (next[groupId]) delete next[groupId];
      else next[groupId] = true;
      set({ collapsedGroups: next });
    },
    setClipboard(patch) {
      set({ clipboard: { ...get().clipboard, ...patch } });
    },
    openDialog(spec) {
      set({ dialogs: [...get().dialogs, { ...spec, key: nextKey++ }], menu: null });
    },
    closeDialog(key) {
      set({ dialogs: get().dialogs.filter((d) => d.key !== key) });
    },
    openMenu(menu) {
      set({ menu });
    },
    closeMenu() {
      const menu = get().menu;
      if (!menu) return;
      set({ menu: null });
      menu.returnFocus?.focus();
    },
    setPrefs(patch) {
      const prefs = { ...get().prefs, ...patch };
      set({ prefs });
      try {
        storage?.setItem(PREFS_KEY, JSON.stringify(prefs));
      } catch {
        // Storage may be full or blocked; preferences then last for the session only.
      }
    },
    setDock(dock) {
      set({ dock, dockRevision: get().dockRevision + 1 });
    },
    bumpDock() {
      set({ dockRevision: get().dockRevision + 1 });
    },
    setBusy(patch) {
      set({ busy: { ...get().busy, ...patch } });
    },
    setAlertOffer(state) {
      set({ alertOffer: state });
    },
    setParentTheme(theme) {
      if (get().themeParent !== theme || get().themeHash !== null) set({ themeParent: theme, themeHash: null });
    },
    setSystemLight(light) {
      if (get().systemLight !== light) set({ systemLight: light });
    },
    setGesture(active) {
      if (get().gesture !== active) set({ gesture: active });
    },
  }));
}
